import { HTMLAttributes, ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize, useLocalizedState } from '@treams/treams-hooks';
import { ContrastIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Checkbox,
	ContextMenu,
	DraggableCard,
	Heading3,
	Highlight,
	IconButton,
	ListIcon,
	MoreAlternateIcon,
	Paragraph,
	PlainInput,
	QuillInput,
	QuillText,
	Select,
	StarIcon,
	TextSizeIcon,
	Tooltip,
	WORKING_AREA_MENU_Z_INDEX,
	WorkingArea,
	tokens,
} from '@treams/treams-ui';

import FeedbackQuestionOptions, {
	FeedbackQuestionOptionsHandle,
	LocalOption,
	OptionsUpdate,
	createOption,
} from './FeedbackQuestionOptions';
import { resolveOptionsUpdate } from './feedbackQuestionOptionText';

const Slot = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
	width: 100%;
	flex: 1 1 auto;
`;

const Header = styled(Flex)`
	justify-content: space-between;
	align-items: flex-start;
	gap: ${tokens.gap.list}px;
	margin-bottom: ${tokens.gap.list}px;
`;

const Actions = styled(Flex)`
	align-items: center;
	flex-shrink: 0;
	gap: ${tokens.gap.element}px;
`;

const TypeSelect = styled.div`
	flex: 0 0 auto;
	width: fit-content;
	max-width: 100%;
`;

const DescriptionPreview = styled(Paragraph)`
	cursor: text;
	display: -webkit-box;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	overflow: hidden;
	overflow-wrap: anywhere;
	margin-top: ${tokens.gap.inline}px;
	color: ${tokens.text.secondary};
`;

const DescriptionInputWrapper = styled(Box)`
	margin-top: ${tokens.gap.inline}px;
	color: ${tokens.text.secondary};
`;

const TitlePlaceholder = styled.span`
	color: ${tokens.text.tertiary};
`;

function toLocalOptions(
	item: { options?: graphql.FeedbackQuestion['options'] | null } | null | undefined,
): LocalOption[] {
	return (item?.options ?? []).map((option) => ({
		key: option.id,
		id: option.id,
		text: option.text,
	}));
}

export interface FeedbackQuestionCardSaveValues {
	text: graphql.LocalizedString;
	subTitle: graphql.LocalizedString;
	additionalDetails: graphql.LocalizedAttributedString;
	type: graphql.FeedbackQuestionType;
	options: graphql.FeedbackQuestionOptionInput[];
}

// The subset of a feedback question the card reads.
export interface FeedbackQuestionCardItem {
	id: string;
	text: graphql.LocalizedString;
	type: graphql.FeedbackQuestionType;
	subTitle?: graphql.FeedbackQuestion['subTitle'];
	additionalDetails?: graphql.FeedbackQuestion['additionalDetails'];
	options?: graphql.FeedbackQuestion['options'];
	category: { id: string };
}

export default function FeedbackQuestionCard<
	T extends FeedbackQuestionCardItem = graphql.FeedbackQuestion,
>(props: {
	item: T;
	new?: boolean;
	query?: string;
	selected?: boolean;
	readOnly?: boolean;
	draggable?: boolean;
	dragging?: boolean;
	loading?: boolean;
	handle?: HTMLAttributes<HTMLElement> | object;
	buttons?: ReactNode;
	canSubjectView?: boolean;
	hideToggleDisabled?: boolean;
	onCancel?: () => Promise<void> | void;
	onCreate?: (item: graphql.FeedbackQuestion) => void;
	onUpdate?: (item: graphql.FeedbackQuestion) => void;
	onSelect?: (item: T) => void;
	onToggleCanSubjectView?: (canSubjectView: boolean) => void;
	// When provided, editing is delegated to this handler instead of persisting
	// via the feedback-question mutations. Used by the composer to keep ad-hoc
	// question edits in memory.
	onSave?: (values: FeedbackQuestionCardSaveValues, isNew: boolean) => Promise<void>;
}) {
	const { t } = useTranslation();
	const { language, localize } = useLocalize();

	const [editing, setEditing] = useState(props.new ?? false);
	const [autoFocus, setAutoFocus] = useState('text');
	const [text, setText] = useState(props.item.text);
	const [localizedText, setLocalizedText] = useLocalizedState(text, setText);
	const [type, setType] = useState(props.item.type);
	const [subTitle, setSubTitle] = useState<graphql.LocalizedString>(props.item.subTitle ?? []);
	const [localizedSubTitle, setLocalizedSubTitle] = useLocalizedState(subTitle, setSubTitle);
	const [additionalDetails, setAdditionalDetails] = useState<graphql.LocalizedAttributedString>(
		props.item.additionalDetails ?? [],
	);
	const [hasContextBlock, setHasContextBlock] = useState(
		() => !QuillText.isEmpty(localize(props.item.additionalDetails ?? [])?.text),
	);
	const [options, setOptions] = useState<LocalOption[]>(() => toLocalOptions(props.item));
	const optionsStateRef = useRef(options);
	const commitOptions = (next: OptionsUpdate) => {
		const resolved = resolveOptionsUpdate(optionsStateRef.current, next);
		optionsStateRef.current = resolved;
		setOptions(resolved);
	};
	const [optionsError, setOptionsError] = useState(false);

	const optionsRef = useRef<FeedbackQuestionOptionsHandle>(null);
	const titleRef = useRef<HTMLDivElement>(null);
	const subTitleRef = useRef<HTMLDivElement>(null);
	const contextRef = useRef<HTMLDivElement>(null);

	const [create, { loading: creating }] = graphql.useCreateFeedbackQuestionMutation();
	const [update, { loading: updating }] = graphql.useUpdateFeedbackQuestionMutation();

	const saving = creating || updating;

	const isMultipleChoice = type === graphql.FeedbackQuestionType.MultipleChoice;
	const dragDisabled = props.draggable === false || (!props.handle && !props.dragging);

	const focusEditableWithin = (wrapper: HTMLElement | null) => {
		const input = wrapper?.querySelector<HTMLElement>(
			'[contenteditable="true"], [contenteditable="plaintext-only"]',
		);

		if (!input) {
			return;
		}

		input.focus();

		const selection = window.getSelection();
		const range = document.createRange();
		range.selectNodeContents(input);
		range.collapse(false);
		selection?.removeAllRanges();
		selection?.addRange(range);
	};

	const focusAfterSubTitle = () => {
		if (hasContextBlock) {
			focusEditableWithin(contextRef.current);
		} else if (isMultipleChoice) {
			optionsRef.current?.focusFirst();
		}
	};

	const focusBeforeOptions = () => {
		if (hasContextBlock) {
			focusEditableWithin(contextRef.current);
		} else {
			focusEditableWithin(subTitleRef.current);
		}
	};

	const reset = () => {
		setText(props.item.text);
		setType(props.item.type);
		setSubTitle(props.item.subTitle ?? []);
		setAdditionalDetails(props.item.additionalDetails ?? []);
		setHasContextBlock(!QuillText.isEmpty(localize(props.item.additionalDetails ?? [])?.text));
		commitOptions(toLocalOptions(props.item));
		setOptionsError(false);
	};

	const onChangeType = (nextType: graphql.FeedbackQuestionType) => {
		setType(nextType);
		setEditing(true);

		if (nextType === graphql.FeedbackQuestionType.MultipleChoice && options.length === 0) {
			commitOptions([createOption(), createOption()]);
		}
	};

	const stopCardClick = (event: { stopPropagation: () => void }) => {
		event.stopPropagation();
	};

	const subTitleText = (localizedSubTitle ?? '').trim();
	const subTitleEmpty = subTitleText === '';

	const contextDelta = localize(additionalDetails)?.text;
	const contextEmpty = QuillText.isEmpty(contextDelta);
	const contextText = (contextDelta?.ops ?? [])
		.map((op) => (typeof op.insert === 'string' ? op.insert : ''))
		.join('')
		.trim();

	return (
		<WorkingArea
			active={editing}
			onCancel={() => {
				reset();
				props.onCancel?.();
				setEditing(false);
			}}
			onSave={async () => {
				const cleanedOptions: graphql.FeedbackQuestionOptionInput[] = isMultipleChoice
					? options
							.filter((option) => option.text.some(({ text }) => text.trim() !== ''))
							.map((option) => ({ id: option.id ?? undefined, text: option.text }))
					: [];

				if (isMultipleChoice && cleanedOptions.length < 2) {
					setOptionsError(true);

					return;
				}

				const cleanedSubTitle = subTitle.filter((entry) => entry.text.trim() !== '');
				const cleanedAdditionalDetails = hasContextBlock
					? additionalDetails.filter((entry) => !QuillText.isEmpty(entry.text))
					: [];

				if (props.onSave) {
					await props.onSave(
						{
							text,
							subTitle: cleanedSubTitle,
							additionalDetails: cleanedAdditionalDetails,
							type,
							options: cleanedOptions,
						},
						props.new ?? false,
					);

					setOptionsError(false);
					setEditing(false);

					return;
				}

				if (props.new) {
					const response = await create({
						variables: {
							input: {
								categoryID: props.item.category.id,
								text,
								subTitle: cleanedSubTitle,
								additionalDetails: cleanedAdditionalDetails,
								type,
								options: cleanedOptions,
							},
						},
					});

					const created = response.data?.createFeedbackQuestion.feedbackQuestion as
						| graphql.FeedbackQuestion
						| undefined;

					if (!created) {
						return;
					}

					commitOptions(toLocalOptions(created));
					props.onCreate?.(created);
				} else {
					const updateInput = {
						id: props.item.id,
						text,
						subTitle: cleanedSubTitle,
						additionalDetails: cleanedAdditionalDetails,
						type,
						options: cleanedOptions,
					};

					const response = await update({
						variables: {
							input: updateInput,
						},
					});

					const updated = response.data?.updateFeedbackQuestion.feedbackQuestion as
						| graphql.FeedbackQuestion
						| undefined;

					if (!updated) {
						return;
					}

					commitOptions(toLocalOptions(updated));
					props.onUpdate?.(updated);
				}

				setOptionsError(false);
				setEditing(false);
			}}
		>
			{(active) => (
				<DraggableCard
					selected={active || props.selected}
					isDragging={props.dragging}
					isDragDisabled={dragDisabled}
					loading={props.loading}
					dragHandleProps={
						editing || dragDisabled
							? undefined
							: (props.handle as HTMLAttributes<HTMLElement> | undefined)
					}
					onClick={props.onSelect ? () => props.onSelect?.(props.item) : undefined}
				>
					<Slot>
						<Header>
							<Flex
								alignItems="center"
								flexWrap="wrap"
								minWidth={0}
								style={{ gap: tokens.gap.list }}
							>
								<TypeSelect
									onMouseDown={stopCardClick}
									onClick={
										props.readOnly
											? stopCardClick
											: (event) => {
													stopCardClick(event);
													setAutoFocus('type');
													setEditing(true);
												}
									}
								>
									<Select
										small
										search={false}
										disabled={props.readOnly}
										selectedValue={type}
										autofocus={active && autoFocus === 'type'}
										zIndex={WORKING_AREA_MENU_Z_INDEX}
										onChange={(value) => {
											if (value) {
												onChangeType(value as graphql.FeedbackQuestionType);
											}
										}}
										options={[
											{
												icon: <TextSizeIcon />,
												title: t('settings.feedbackLibrary.question.type.text'),
												value: graphql.FeedbackQuestionType.Text,
											},
											{
												icon: <StarIcon />,
												title: t('settings.feedbackLibrary.question.type.star'),
												value: graphql.FeedbackQuestionType.Stars,
											},
											{
												icon: <ContrastIcon />,
												title: t('settings.feedbackLibrary.question.type.both'),
												value: graphql.FeedbackQuestionType.Both,
											},
											{
												icon: <ListIcon />,
												title: t('settings.feedbackLibrary.question.type.multipleChoice'),
												value: graphql.FeedbackQuestionType.MultipleChoice,
											},
										]}
									/>
								</TypeSelect>
								{props.onToggleCanSubjectView && (
									<Tooltip
										content={t(
											'settings.feedbackLibrary.template.questions.hideFromEmployee.tooltip',
										)}
										portal
										position="bottom"
									>
										<Box onMouseDown={stopCardClick} onClick={stopCardClick}>
											<Checkbox
												checked={!(props.canSubjectView ?? true)}
												disabled={props.hideToggleDisabled}
												onClick={() =>
													props.hideToggleDisabled
														? undefined
														: props.onToggleCanSubjectView?.(!(props.canSubjectView ?? true))
												}
												label={t(
													'settings.feedbackLibrary.template.questions.hideFromEmployee.label',
												)}
											/>
										</Box>
									</Tooltip>
								)}
							</Flex>
							<Actions onMouseDown={stopCardClick} onClick={stopCardClick}>
								{!props.readOnly && (
									<ContextMenu.Target
										disabled={saving}
										zIndex={WORKING_AREA_MENU_Z_INDEX}
										menu={
											<ContextMenu>
												{hasContextBlock ? (
													<ContextMenu.Item
														type="destructive"
														title={t('settings.feedbackLibrary.question.contextBlock.remove')}
														onClick={() => {
															setAdditionalDetails([]);
															setHasContextBlock(false);
														}}
													/>
												) : (
													<ContextMenu.Item
														title={t('settings.feedbackLibrary.question.contextBlock.add')}
														onClick={() => {
															setAutoFocus('context');
															setHasContextBlock(true);
															setEditing(true);
														}}
													/>
												)}
											</ContextMenu>
										}
									>
										<IconButton type="plain" icon={<MoreAlternateIcon />} />
									</ContextMenu.Target>
								)}
								{props.buttons}
							</Actions>
						</Header>
						<Box ref={titleRef}>
							<Heading3>
								{props.readOnly ? (
									localizedText
								) : active ? (
									<PlainInput
										multiline={false}
										onChange={setLocalizedText}
										onFocus={() => {
											setAutoFocus('text');
											setEditing(true);
										}}
										onKeyDown={(event) => {
											if (event.key === 'Tab' && !event.shiftKey) {
												event.preventDefault();
												focusEditableWithin(subTitleRef.current);
											} else if (event.key === 'Enter') {
												event.preventDefault();
											}
										}}
										autoFocus={active && autoFocus === 'text'}
										value={localizedText}
										placeholder={t('settings.feedbackLibrary.question.text.placeholder')}
									/>
								) : (
									<Box
										onMouseDown={() => {
											setAutoFocus('text');
											setEditing(true);
										}}
									>
										{localizedText ? (
											<Highlight query={props.query}>{localizedText}</Highlight>
										) : (
											<TitlePlaceholder>
												{t('settings.feedbackLibrary.question.text.placeholder')}
											</TitlePlaceholder>
										)}
									</Box>
								)}
							</Heading3>
						</Box>
						{active && !props.readOnly ? (
							<DescriptionInputWrapper ref={subTitleRef}>
								<PlainInput
									multiline={false}
									onChange={setLocalizedSubTitle}
									onFocus={() => {
										setAutoFocus('subTitle');
										setEditing(true);
									}}
									onKeyDown={(event) => {
										if (
											event.key === 'Tab' &&
											!event.shiftKey &&
											(hasContextBlock || isMultipleChoice)
										) {
											event.preventDefault();
											focusAfterSubTitle();
										} else if (event.key === 'Tab' && event.shiftKey) {
											event.preventDefault();
											focusEditableWithin(titleRef.current);
										} else if (event.key === 'Enter') {
											event.preventDefault();
										}
									}}
									autoFocus={active && autoFocus === 'subTitle'}
									value={localizedSubTitle}
									placeholder={t('settings.feedbackLibrary.question.description.placeholder')}
								/>
							</DescriptionInputWrapper>
						) : (
							!subTitleEmpty && (
								<DescriptionPreview
									onMouseDown={() => {
										if (props.readOnly) {
											return;
										}

										setAutoFocus('subTitle');
										setEditing(true);
									}}
								>
									{subTitleText}
								</DescriptionPreview>
							)
						)}
						{hasContextBlock &&
							(active && !props.readOnly ? (
								<Box ref={contextRef} mt={`${tokens.gap.list}px`}>
									<QuillInput.Localizable
										autoFocus={autoFocus === 'context'}
										maxHeight={200}
										value={additionalDetails}
										onChange={setAdditionalDetails}
										keyboardShortcuts={[
											{
												key: 'Tab',
												shiftKey: true,
												handler: () => focusEditableWithin(subTitleRef.current),
											},
											...(isMultipleChoice
												? [{ key: 'Tab', handler: () => optionsRef.current?.focusFirst() }]
												: []),
										]}
										placeholder={t('settings.feedbackLibrary.question.contextBlock.placeholder')}
									/>
								</Box>
							) : (
								!contextEmpty && (
									<DescriptionPreview
										style={{ marginTop: tokens.gap.list }}
										onMouseDown={() => {
											if (props.readOnly) {
												return;
											}

											setAutoFocus('context');
											setEditing(true);
										}}
									>
										{contextText}
									</DescriptionPreview>
								)
							))}
						{isMultipleChoice && (
							<FeedbackQuestionOptions
								ref={optionsRef}
								options={options}
								languageID={language.id}
								active={active}
								readOnly={props.readOnly}
								autoFocus={autoFocus}
								error={
									optionsError
										? t('settings.feedbackLibrary.question.options.error.minimum')
										: undefined
								}
								onChange={(next) => {
									commitOptions(next);
									setOptionsError(false);
								}}
								onFocusOption={(key) => {
									if (props.readOnly) {
										return;
									}

									setAutoFocus(`option:${key}`);
									setEditing(true);
								}}
								onFocusPrevious={() => {
									if (props.readOnly) {
										return;
									}

									focusBeforeOptions();
								}}
								onAdd={() => setEditing(true)}
							/>
						)}
					</Slot>
				</DraggableCard>
			)}
		</WorkingArea>
	);
}
